/* @jsx h */
import { defineComponent, h, PropType } from "vue";
import type { ReservationSettingsUpdateHandler } from "@lemmonhq/types";

export const ReviewRequest = defineComponent({
  name: "ReviewRequest",
  props: {
    enabled: { type: Boolean, required: true },
    delayHours: { type: Number, required: true },
    minDaysBetween: { type: Number, required: true },
    customMessage: { type: String, required: true },
    onUpdate: {
      type: Function as PropType<ReservationSettingsUpdateHandler>,
      required: true,
    },
  },
  setup(props) {
    return () => (
      <div class="space-y-6 py-2">
        {/* Enable Toggle */}
        <div class="flex items-center justify-between">
          <div>
            <h4 class="font-medium text-base-content">
              Send review requests
            </h4>
            <p class="text-sm text-base-content/60">
              Automatically ask customers to leave a Google review after their
              visit is completed
            </p>
          </div>
          <input
            type="checkbox"
            class="toggle toggle-primary"
            checked={props.enabled}
            onChange={(e: any) =>
              props.onUpdate("review_request_enabled", e.target.checked)
            }
          />
        </div>

        {props.enabled && (
          <div class="space-y-5 pl-1 border-l-2 border-primary/20 ml-2 pl-4">
            {/* Delay Hours */}
            <div>
              <label class="text-sm font-medium text-base-content">
                Delay after completion (hours)
              </label>
              <p class="text-xs text-base-content/50 mb-2">
                How long to wait after the reservation is marked as completed
                before sending the email
              </p>
              <input
                type="number"
                class="input input-bordered input-sm w-32"
                min={0}
                max={72}
                value={props.delayHours}
                onInput={(e: any) =>
                  props.onUpdate(
                    "review_request_delay_hours",
                    parseInt(e.target.value) || 0,
                  )
                }
              />
            </div>

            {/* Min Days Between */}
            <div>
              <label class="text-sm font-medium text-base-content">
                Minimum days between requests
              </label>
              <p class="text-xs text-base-content/50 mb-2">
                Don't send another review request to the same customer within
                this many days
              </p>
              <input
                type="number"
                class="input input-bordered input-sm w-32"
                min={1}
                max={365}
                value={props.minDaysBetween}
                onInput={(e: any) =>
                  props.onUpdate(
                    "review_request_min_days_between",
                    parseInt(e.target.value) || 30,
                  )
                }
              />
            </div>

            {/* Custom Message */}
            <div>
              <label class="text-sm font-medium text-base-content">
                Custom message
              </label>
              <p class="text-xs text-base-content/50 mb-2">
                Optional personal message from your restaurant included in the
                review request email
              </p>
              <textarea
                class="textarea textarea-bordered w-full"
                rows={3}
                placeholder="e.g. We loved having you at our restaurant! Your opinion helps us improve and grow..."
                value={props.customMessage}
                onInput={(e: any) =>
                  props.onUpdate("review_request_message", e.target.value)
                }
              />
            </div>

            {/* Info box */}
            <div class="bg-info/10 border border-info/20 rounded-lg p-3">
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-info text-lg mt-0.5">
                  info
                </span>
                <div class="text-xs text-base-content/70">
                  <p>
                    Review requests are only sent when a reservation is marked as
                    completed (not for no-shows or cancellations). A Google Place
                    ID must be configured in the Google Reviews section above for
                    this to work.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
});
